import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AuthLayout from "@/components/layouts/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: `${window.location.origin}/create-new-password`,
      });

      if (error) {
        throw error;
      }
      
      setIsEmailSent(true);
      toast({
        title: "Reset link sent!",
        description: `We've sent a password reset link to ${values.email}`,
      });
    } catch (error) {
      console.error("Password reset error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send reset link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex min-h-screen items-center justify-center px-4 bg-white">
        <div className="w-full max-w-[621px] mt-[20px] md:mt-[72px] shadow-md p-8 md:p-[56px] rounded-md mb-16">
          <div className="text-left mb-8">
            <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
              Reset password?
            </h1>
            <p className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
              {isEmailSent 
                ? "Check your email for a reset link"
                : "Enter the email address associated with your account and we'll send you a link to reset your password"
              }
            </p>
          </div>

          {!isEmailSent ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Email"
                          className="h-[56px] px-4 text-[14px] md:text-base border-gray-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Link
                  to="/login"
                  className="block text-left mt-4 text-sm text-[#6C4F77] hover:underline font-bold"
                >
                  Return to login
                </Link>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#6C4F77] hover:bg-[#5A3A68] text-white font-bold text-[14px] font-modernist !mt-[48px] py-[20px]"
                >
                  {isLoading ? "Sending..." : "Continue"}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="text-center">
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-700">
                  We've sent a password reset link to <strong>{form.getValues("email")}</strong>
                </p>
              </div>
              <Button
                onClick={() => {
                  setIsEmailSent(false);
                  form.reset();
                }}
                className="w-full bg-[#6C4F77] hover:bg-[#5A3A68] text-white font-bold text-[14px] font-modernist py-[20px]"
              >
                Try another email
              </Button>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;